import { useContext, useState, useEffect, type FC } from 'react';
import { useFragment, graphql } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';
import { Header } from 'dibs-elements/exports/Header';
import { ErrorMessage } from 'dibs-elements/exports/ErrorMessage';
import { StepNavigator, type Step } from 'dibs-elements/exports/StepNavigator';
import SellerFormContext from './SellerFormContext';
import SellerApplicationFormUserSelection from './SellerApplicationFormUserSelection';
import SellerApplicationBusinessIntro from './SellerApplicationBusinessIntro';
import SellerApplicationContactDetails, {
    stepTitle as contactDetailsStepTitle,
} from './SellerApplicationContactDetails';
import SellerApplicationBusinessDetails, {
    stepTitle as businessDetailsStepTitle,
} from './SellerApplicationBusinessDetails';
import SellerApplicationBusinessDetailsContinued, {
    headerTitle as businessDetailsContinuedHeaderTitle,
} from './SellerApplicationBusinessDetailsContinued';
import SellerApplicationFormInventoryDetails, {
    stepTitle as inventoryDetailsStepTitle,
} from './SellerApplicationFormInventoryDetails';
import SellerApplicationFormAdditionalDetails, {
    headerTitle as additionalDetailsHeaderTitle,
    stepTitle as additionalDetailsStepTitle,
} from './SellerApplicationFormAdditionalDetails';
import SellerApplicationBusinessConfirmation from './SellerApplicationBusinessConfirmation';
import { ConsignorsRestrictionMessage } from './ConsignorsRestrictionMessage';

import { type SellerApplicationFormSelector_viewer$key } from './__generated__/SellerApplicationFormSelector_viewer.graphql';

import styles from './styles/SellerApplicationFormSelector.scss';

type Props = { viewer: SellerApplicationFormSelector_viewer$key };

const SellerApplicationFormSelector: FC<Props> = ({ viewer: viewerRef }) => {
    const viewer = useFragment(
        graphql`
            fragment SellerApplicationFormSelector_viewer on Viewer {
                ...SellerApplicationContactDetails_viewer
                ...SellerApplicationFormInventorySample_viewer
            }
        `,
        viewerRef
    );
    const { state } = useContext(SellerFormContext);
    const [formError, setFormError] = useState(false);

    useEffect(() => {
        setFormError(false);
    }, [state.step]);

    let component;
    let currentIndicatorStep = 0;
    switch (state.step) {
        case 'BUSINESS_INTRO':
            component = <SellerApplicationBusinessIntro />;
            break;
        case 'BUSINESS_STEP1':
            currentIndicatorStep = 0;
            component = (
                <SellerApplicationContactDetails setFormError={setFormError} viewer={viewer} />
            );
            break;
        case 'BUSINESS_STEP2':
            currentIndicatorStep = 1;
            component = <SellerApplicationBusinessDetails setFormError={setFormError} />;
            break;
        case 'BUSINESS_STEP3':
            currentIndicatorStep = 1;
            component = <SellerApplicationBusinessDetailsContinued setFormError={setFormError} />;
            break;
        case 'BUSINESS_STEP4':
            currentIndicatorStep = 2;
            component = (
                <SellerApplicationFormInventoryDetails
                    setFormError={setFormError}
                    viewer={viewer}
                />
            );
            break;
        case 'BUSINESS_STEP5':
            currentIndicatorStep = 3;
            component = <SellerApplicationFormAdditionalDetails setFormError={setFormError} />;
            break;
        case 'CONSIGNORS_RESTRICTION_MESSAGE':
            component = <ConsignorsRestrictionMessage />;
            break;
        case 'BUSINESS_CONFIRMATION':
            component = <SellerApplicationBusinessConfirmation />;
            break;
        default:
            component = <SellerApplicationFormUserSelection />;
            break;
    }

    const indicatorSteps: Step[] = [
        {
            label: contactDetailsStepTitle,
        },
        {
            label: businessDetailsStepTitle,
        },
        {
            label: inventoryDetailsStepTitle,
        },
        {
            label: additionalDetailsStepTitle,
        },
    ];

    let headerTitle;
    if (state.step === 'BUSINESS_STEP3') {
        headerTitle = businessDetailsContinuedHeaderTitle;
    } else if (currentIndicatorStep === 3) {
        headerTitle = additionalDetailsHeaderTitle;
    } else {
        headerTitle = indicatorSteps[currentIndicatorStep].label;
    }

    return (
        <div className={styles.formStepsWrapper}>
            {state.step && state.step.includes('BUSINESS_STEP') ? (
                <div className={styles.headerWrapper}>
                    <StepNavigator
                        dataTn="seller-app-step-navigator"
                        steps={indicatorSteps}
                        currentStep={currentIndicatorStep}
                        disableNavigation
                    />
                    <div className={styles.divider} />
                    <Header title={headerTitle} />
                </div>
            ) : null}
            {formError ? (
                <div className={styles.formError}>
                    <ErrorMessage
                        dataTn="SellerApplicationForm-request-error"
                        message={
                            <FormattedMessage
                                defaultMessage="Something went wrong. Please try again or contact support."
                                id="SellerApplicationForm.requestError"
                            />
                        }
                    />
                </div>
            ) : null}
            {component}
        </div>
    );
};

export default SellerApplicationFormSelector;
